import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { useNavigate, useParams } from "react-router-dom";
import TopBar from "@/components/TopBar";
import StickyBottom from "@/components/StickyBottom";
import FlowBadges from "@/components/steps/FlowBadges";
import Step1Customer from "@/components/steps/Step1Customer";
import Step2Rooms from "@/components/steps/Step2Rooms";
import Step3Accessories from "@/components/steps/Step3Accessories";
import Step4Remarks from "@/components/steps/Step4Remarks";
import Step5SelectTiers from "@/components/steps/Step5SelectTiers";
import Step5Output from "@/components/steps/Step5Output";
import { api, sqft } from "@/lib/quotationApi";
import { syncFinishSnapshots } from "@/lib/shareText";
import { loadDraft, saveDraft, clearDraft } from "@/lib/draftStorage";
import { useQuotationActions } from "@/hooks/useQuotationActions";
import { useMasterData } from "@/hooks/useMasterData";
import { useBranding } from "@/lib/branding";
import { useAuth } from "@/lib/auth";

const STEP_TITLES = ["Customer", "Areas", "Accessories", "Remarks", "Tiers", "Summary"];
const TOTAL_STEPS = 6;

const emptyCustomer = {
  name: "",
  location: "",
  city: "",
  mobile: "",
  quotation_given_by: "",
};

function uid() {
  return Math.random().toString(36).slice(2, 10);
}

const initialState = () => ({
  customer: { ...emptyCustomer },
  rooms: [
    {
      id: uid(),
      area_type: "Kitchen",
      description: "",
      measurements: [],
      images: [],
    },
  ],
  accessories: [],
  remarks: "",
  discount_amount: "",
  tax_percent: "",
  selected_tier_ids: [],
  payment_plan_id: "",
});

// Static motion.div transition — extracted to avoid re-creating the object on every render.
const STEP_TRANSITION = { type: "spring", stiffness: 320, damping: 32 };

// Step 5 ("Tier select") next-button label depends on whether the quotation
// has already been saved (so user is editing) or is fresh.
function getStep5NextLabel(step, hasSaved) {
  if (step !== 5) return "Continue";
  return hasSaved ? "Review changes" : "Generate Quotation";
}

// Step 6 floating Save label — three states: actively saving, updating an
// existing quote, or first-time save.
function getSaveButtonLabel(saving, hasSaved) {
  if (saving) return hasSaved ? "Updating…" : "Saving…";
  return hasSaved ? "Update quotation" : "Save quotation";
}

// When editing an existing quotation, prefer the tier config snapshotted onto
// that quote so the user sees the same tier options/discounts they originally
// used. New admin tier changes won't leak into the saved quote on re-save.
function resolveEffectiveTierConfig(editingExisting, savedTiersSnapshot, tierConfigV2) {
  if (
    editingExisting &&
    savedTiersSnapshot &&
    Array.isArray(savedTiersSnapshot.list) &&
    savedTiersSnapshot.list.length
  ) {
    return {
      tiers: savedTiersSnapshot.list.map((t, i) => ({
        id: t.id,
        name: t.name,
        description: t.description || "",
        color: t.color || "#C5A880",
        finish_discount: t.finish_discount || 0,
        hardware_discount: t.hardware_discount || 0,
        modular_multiplier: t.modular_multiplier,
        is_base: !!t.is_base,
        order: i,
      })),
      modular_multiplier: savedTiersSnapshot.modular_multiplier ?? 2.5,
    };
  }
  return tierConfigV2;
}

export default function QuotationFlow() {
  const params = useParams();
  const navigate = useNavigate();
  const editingExisting = Boolean(params.id);

  const [step, setStep] = useState(editingExisting ? 6 : 1);
  const [data, setData] = useState(() => {
    if (editingExisting) return initialState();
    const draft = loadDraft();
    return draft ? { ...initialState(), ...draft } : initialState();
  });
  const { finishes, masterAccessories, tierConfigV2, paymentPlans } = useMasterData();
  const [savedTiersSnapshot, setSavedTiersSnapshot] = useState(null);
  const [savedId, setSavedId] = useState(null);
  const [savedStatus, setSavedStatus] = useState("draft");
  const [dirty, setDirty] = useState(false);
  const [loadingExisting, setLoadingExisting] = useState(editingExisting);
  const [dir, setDir] = useState(1);
  const { branding, logoUrl } = useBranding();
  const { can, role } = useAuth();
  const showApprovalTrail = role === "admin" || role === "manager";
  const [approvedByName, setApprovedByName] = useState(null);
  const [approvedAt, setApprovedAt] = useState(null);
  const [activity, setActivity] = useState([]);

  // When editing an existing quotation, prefer the tier config that was
  // snapshotted onto that quote (see resolveEffectiveTierConfig above).
  const effectiveTierConfigV2 = resolveEffectiveTierConfig(
    editingExisting,
    savedTiersSnapshot,
    tierConfigV2,
  );

  const {
    saving,
    approving,
    downloading,
    onApprove,
    onUnapprove,
    saveQuotation,
    updateQuotation,
    onShareWhatsApp,
    onDownloadPDF,
  } = useQuotationActions({
    data,
    branding,
    logoUrl,
    paymentPlans,
    savedId,
    savedStatus,
    savedTiersSnapshot,
    effectiveTierConfigV2,
    activity,
    setSavedId,
    setSavedStatus,
    setSavedTiersSnapshot,
    setApprovedByName,
    setApprovedAt,
    setActivity,
    setDirty,
  });

  useEffect(() => {
    if (!editingExisting) return;
    (async () => {
      try {
        const r = await api.get(`/quotations/${params.id}`);
        setData({
          customer: r.data.customer,
          rooms: (r.data.rooms || []).map((room) => ({
            ...room,
            images: room.images || [],
          })),
          accessories: r.data.accessories || [],
          remarks: r.data.remarks || "",
          discount_amount: r.data.discount_amount || "",
          tax_percent: r.data.tax_percent || "",
          selected_tier_ids:
            r.data.selected_tier_ids ||
            (r.data.tiers && Array.isArray(r.data.tiers.list)
              ? r.data.tiers.list.map((t) => t.id)
              : r.data.tiers && r.data.tiers.selected_tier_ids) ||
            [],
          payment_plan_id: r.data.payment_plan_id || r.data.payment_plan?.id || "",
        });
        setSavedTiersSnapshot(r.data.tiers || null);
        setSavedId(r.data.id);
        setSavedStatus(r.data.status || "draft");
        setApprovedByName(r.data.approved_by_name || null);
        setApprovedAt(r.data.approved_at || null);
        setActivity(r.data.activity || []);
        setDirty(false);
      } catch {
        toast.error("Quotation not found");
        navigate("/history");
      } finally {
        setLoadingExisting(false);
      }
    })();
  }, [editingExisting, params.id, navigate]);

  useEffect(() => {
    if (editingExisting || savedId) return;
    saveDraft(data);
  }, [data, editingExisting, savedId]);

  // For NEW (unsaved) quotes only: keep each measurement's finish_tier_prices
  // snapshot in sync with the current master finishes table. This handles the
  // common workflow where the admin sets up per-tier prices AFTER measurements
  // were already added (the form state otherwise carries an empty snapshot
  // taken when the finish was first picked). Saved quotes are left untouched
  // — price-freeze is honoured.
  useEffect(() => {
    if (editingExisting || savedId) return;
    if (!finishes || finishes.length === 0) return;
    setData((d) => syncFinishSnapshots(d, finishes));
  }, [finishes, editingExisting, savedId]);

  const markDirty = () => {
    if (savedId && !dirty) setDirty(true);
  };

  const setCustomer = (c) => {
    markDirty();
    setData((d) => ({ ...d, customer: c }));
  };
  const setRooms = (r) => {
    markDirty();
    setData((d) => ({ ...d, rooms: r }));
  };
  const setAccessories = (a) => {
    markDirty();
    setData((d) => ({ ...d, accessories: a }));
  };
  const setRemarks = (r) => {
    markDirty();
    setData((d) => ({ ...d, remarks: r }));
  };
  const setDataDirty = (next) => {
    markDirty();
    setData(next);
  };
  const setPaymentPlanId = (id) => {
    markDirty();
    setData((d) => ({ ...d, payment_plan_id: id || "" }));
  };

  const canProceed = () => {
    if (step === 1) {
      const c = data.customer;
      return c.name.trim() && c.mobile.trim();
    }
    if (step === 2) {
      return (
        data.rooms.length > 0 &&
        data.rooms.every(
          (r) =>
            r.area_type &&
            r.measurements.length > 0 &&
            r.measurements.every((m) => sqft(m) > 0 && m.finish_id)
        )
      );
    }
    if (step === 5) {
      return (data.selected_tier_ids || []).length > 0;
    }
    return true;
  };

  const onNext = () => {
    if (!canProceed()) {
      if (step === 1) toast.error("Please enter name and mobile");
      else if (step === 2) toast.error("Each measurement needs length, height and finish");
      else if (step === 5) toast.error("Pick at least one tier to quote");
      return;
    }
    setDir(1);
    setStep((s) => Math.min(TOTAL_STEPS, s + 1));
  };
  const onBack = () => {
    setDir(-1);
    setStep((s) => Math.max(1, s - 1));
  };

  const toggleTier = (id) => {
    markDirty();
    setData((d) => {
      const cur = d.selected_tier_ids || [];
      const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
      return { ...d, selected_tier_ids: next };
    });
  };
  const selectAllTiers = () => {
    markDirty();
    setData((d) => ({
      ...d,
      selected_tier_ids: (effectiveTierConfigV2.tiers || []).map((t) => t.id),
    }));
  };
  const clearTiers = () => {
    markDirty();
    setData((d) => ({ ...d, selected_tier_ids: [] }));
  };

  const resetAll = () => {
    clearDraft();
    setData(initialState());
    setSavedId(null);
    setDirty(false);
    setStep(1);
    navigate("/new");
    toast.success("Started a new quotation");
  };

  if (loadingExisting) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white/50">
        Loading quotation...
      </div>
    );
  }

  // On the summary step:
  //   - no savedId → Save (POST)
  //   - savedId + dirty → Update (PUT)
  //   - savedId + !dirty → show "Saved"
  const hasSaved = Boolean(savedId);
  const onSummarySave = hasSaved ? updateQuotation : saveQuotation;

  return (
    <div className="min-h-screen pb-32 pt-20">
      <TopBar step={step} totalSteps={TOTAL_STEPS} title={STEP_TITLES[step - 1]} editing={editingExisting} />

      <main className="max-w-2xl mx-auto px-4">
        <FlowBadges editingExisting={editingExisting} savedId={savedId} dirty={dirty} />
        <AnimatePresence mode="wait" initial={false} custom={dir}>
          <motion.div
            key={step}
            custom={dir}
            initial={{ opacity: 0, x: dir * 40 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: dir * -40 }}
            transition={STEP_TRANSITION}
          >
            {step === 1 && <Step1Customer data={data.customer} onChange={setCustomer} />}
            {step === 2 && (
              <Step2Rooms
                rooms={data.rooms}
                setRooms={setRooms}
                finishes={finishes}
                modularMultiplier={effectiveTierConfigV2?.modular_multiplier ?? 2.5}
              />
            )}
            {step === 3 && (
              <Step3Accessories
                accessories={data.accessories}
                setAccessories={setAccessories}
                masterAccessories={masterAccessories}
              />
            )}
            {step === 4 && <Step4Remarks remarks={data.remarks} setRemarks={setRemarks} />}
            {step === 5 && (
              <Step5SelectTiers
                data={data}
                tierConfigV2={effectiveTierConfigV2}
                selectedTierIds={data.selected_tier_ids || []}
                onToggleTier={toggleTier}
                onSelectAll={selectAllTiers}
                onSelectNone={clearTiers}
                paymentPlans={paymentPlans}
                paymentPlanId={data.payment_plan_id || ""}
                onSelectPaymentPlan={setPaymentPlanId}
              />
            )}
            {step === 6 && (
              <Step5Output
                data={data}
                savedId={savedId}
                savedStatus={savedStatus}
                canApprove={can("quotations.approve")}
                onApprove={onApprove}
                onUnapprove={onUnapprove}
                approving={approving}
                showApprovalTrail={showApprovalTrail}
                approvedByName={approvedByName}
                approvedAt={approvedAt}
                activity={activity}
                editing={hasSaved}
                dirty={dirty}
                tierConfigV2={effectiveTierConfigV2}
                selectedTierIds={data.selected_tier_ids || []}
                tiersSnapshot={savedTiersSnapshot}
                paymentPlans={paymentPlans}
                onSave={onSummarySave}
                onShareWhatsApp={onShareWhatsApp}
                onDownloadPDF={onDownloadPDF}
                onChange={setDataDirty}
                saving={saving}
                downloading={downloading}
              />
            )}
          </motion.div>
        </AnimatePresence>
      </main>

      {step < 6 ? (
        <StickyBottom
          onNext={onNext}
          onBack={onBack}
          showBack={step > 1}
          nextLabel={getStep5NextLabel(step, hasSaved)}
          nextDisabled={!canProceed()}
        />
      ) : (
        <StickyBottom
          onBack={onBack}
          showBack={true}
          rightContent={
            (hasSaved && !dirty) ? (
              <button
                onClick={resetAll}
                className="btn-primary w-full"
                data-testid="new-quotation-btn"
              >
                New quotation
              </button>
            ) : (
              <button
                type="button"
                onClick={onSummarySave}
                disabled={saving || (hasSaved && !dirty)}
                className="btn-primary w-full flex items-center justify-center gap-2"
                data-testid="floating-save-quotation-btn"
              >
                {getSaveButtonLabel(saving, hasSaved)}
              </button>
            )
          }
        />
      )}
    </div>
  );
}
